/**
 * Human-readable filenames for audit-bundle downloads.
 *
 * Shared by the API (`GET /v1/audit/:record_id/bundle` sets
 * Content-Disposition with it) and the dashboard (which receives the
 * body as a Blob and cannot read Content-Disposition cross-origin, so it
 * computes the same name locally). Keeping the shape in one place is how
 * server and client stay in agreement.
 *
 * Shape (the bundle covers every record in one site + chain + UTC day):
 *   `sill-audit-{domain}-{YYYY-MM-DD}-{chain}.{ext}`
 *   e.g. `sill-audit-northwind-coffee.example-2026-10-03-transactional.html`
 *
 * A day split into parts (ADR-0227) adds the part before the extension:
 *   `sill-audit-{domain}-{YYYY-MM-DD}-{chain}-part-{k}-of-{n}.{ext}`
 *
 * Domain sanitization: lowercase; keep `[a-z0-9.-]`; collapse any other
 * run to a single `-`; trim leading/trailing `-` and `.`; cap at 80 chars.
 * A missing domain (or one that sanitizes to nothing) renders as
 * `unknown-site`, so the name never degrades to `sill-audit--…`.
 */
/** Fallback label when the merchant domain is missing or unusable. */
export const UNKNOWN_DOMAIN_LABEL = 'unknown-site';
const MAX_DOMAIN_CHARS = 80;
/** Lowercased, filesystem-safe form of a merchant domain. */
export function sanitizeDomainForFilename(domain) {
    if (typeof domain !== 'string')
        return UNKNOWN_DOMAIN_LABEL;
    const sanitized = domain
        .toLowerCase()
        .replace(/[^a-z0-9.-]+/g, '-')
        .slice(0, MAX_DOMAIN_CHARS)
        .replace(/^[-.]+/, '')
        .replace(/[-.]+$/, '');
    return sanitized.length > 0 ? sanitized : UNKNOWN_DOMAIN_LABEL;
}
/** Last six characters of a record id, lowercased, `[a-z0-9]` only. */
export function recordIdSuffix(record_id) {
    if (typeof record_id !== 'string')
        return '';
    const tail = record_id.toLowerCase().replace(/[^a-z0-9]/g, '').slice(-6);
    return tail.length > 0 ? `-${tail}` : '';
}
/** Compose the day-bundle download filename. See module docstring. */
export function auditBundleFilename(parts) {
    const domain = sanitizeDomainForFilename(parts.domain);
    const p = parts.part;
    const part_suffix = p !== undefined
        && Number.isSafeInteger(p.index)
        && Number.isSafeInteger(p.count)
        && p.count > 1
        && p.index >= 1
        && p.index <= p.count
        ? `-part-${String(p.index)}-of-${String(p.count)}`
        : '';
    const rec_suffix = recordIdSuffix(parts.record_id);
    const narrowed_suffix = parts.narrowed === 'attack'
        ? '-narrowed-attack'
        : parts.narrowed === 'caller'
            ? '-narrowed-caller'
            : '';
    return `sill-audit-${domain}-${parts.utc_date}-${parts.chain}${part_suffix}${narrowed_suffix}${rec_suffix}.${parts.ext}`;
}
//# sourceMappingURL=filenames.js.map